import { z } from 'zod'

// ===========================================
// Schemas de validación - Rondela
// ===========================================

// Helpers
const nonEmptyString = (fieldName: string) =>
  z.string().min(1, { message: `${fieldName} es requerido` })

const email = z.string().email({ message: 'Email inválido' })

// Helper for optional time field - allows empty string, transforms to undefined
const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/
const optionalTimeField = z.string()
  .optional()
  .transform(val => (!val || val === '') ? undefined : val)
  .refine(val => val === undefined || timeRegex.test(val), { message: 'Formato HH:MM' })

// For update schemas where field can be explicitly set to null
const nullableTimeField = z.string()
  .nullable()
  .optional()
  .transform(val => (!val || val === '') ? null : val)
  .refine(val => val === null || val === undefined || timeRegex.test(val), { message: 'Formato HH:MM' })

// Available for future use:
// const optionalDate = z.string().datetime().optional()
// const requiredDate = z.string().datetime({ message: 'Fecha inválida' })

// ===========================================
// Auth Schemas
// ===========================================

export const requestMagicLinkSchema = z.object({
  email: email,
})

export const verifyMagicLinkSchema = z.object({
  token: nonEmptyString('Token'),
})

// ===========================================
// User Schemas
// ===========================================

export const userRoleSchema = z.enum(['admin', 'cuidador'])

export const createUserSchema = z.object({
  email: email,
  nombre: nonEmptyString('Nombre'),
  role: userRoleSchema,
  careGroupId: nonEmptyString('ID del grupo'),
})

export const updateUserSchema = z.object({
  nombre: nonEmptyString('Nombre').optional(),
  role: userRoleSchema.optional(),
})

// ===========================================
// Care Group Schemas
// ===========================================

export const createCareGroupSchema = z.object({
  nombre: nonEmptyString('Nombre del grupo'),
  descripcion: z.string().optional(),
})

export const updateCareGroupSchema = z.object({
  nombre: nonEmptyString('Nombre del grupo').optional(),
  descripcion: z.string().nullable().optional(),
})

// ===========================================
// Group Invitation Schemas
// ===========================================

export const createInvitationSchema = z.object({
  email: email,
  role: userRoleSchema.optional().default('cuidador'),
})

export const invitationTokenSchema = z.object({
  token: nonEmptyString('Token'),
})

// ===========================================
// Pet Schemas
// ===========================================

export const petSpeciesSchema = z.enum(['perro', 'gato', 'otro'])

export const createPetSchema = z.object({
  nombre: nonEmptyString('Nombre'),
  especie: petSpeciesSchema.optional().default('perro'),
  raza: z.string().optional(),
  fechaNacimiento: z.string().optional(),
  peso: z.number().positive().optional(),
  condicionesMedicas: z.string().optional(),
  notas: z.string().optional(),
})

export const updatePetSchema = z.object({
  nombre: nonEmptyString('Nombre').optional(),
  especie: petSpeciesSchema.optional(),
  raza: z.string().nullable().optional(),
  fechaNacimiento: z.string().nullable().optional(),
  peso: z.number().positive().nullable().optional(),
  fotoUrl: z.string().url().nullable().optional(),
  condicionesMedicas: z.string().nullable().optional(),
  notas: z.string().nullable().optional(),
  status: z.enum(['active', 'inactive']).optional(),
})

// ===========================================
// Medication Schemas
// ===========================================

export const medicationFrequencySchema = z.enum([
  'una_vez',
  'diaria',
  'cada_12h',
  'cada_8h',
  'semanal',
  'mensual',
  'segun_necesidad',
])

export const createMedicationSchema = z.object({
  nombre: nonEmptyString('Nombre del medicamento'),
  dosis: nonEmptyString('Dosis'),
  frecuencia: medicationFrequencySchema,
  fechaInicio: z.string({ message: 'Fecha de inicio es requerida' }),
  fechaFin: z.string().optional(),
  instrucciones: z.string().optional(),
  horaPreferida: optionalTimeField,
})

export const updateMedicationSchema = z.object({
  nombre: nonEmptyString('Nombre del medicamento').optional(),
  dosis: nonEmptyString('Dosis').optional(),
  frecuencia: medicationFrequencySchema.optional(),
  fechaFin: z.string().nullable().optional(),
  instrucciones: z.string().nullable().optional(),
  horaPreferida: nullableTimeField,
  activa: z.boolean().optional(),
  completada: z.boolean().optional(),
})

export const logMedicationSchema = z.object({
  notas: z.string().optional(),
})

// ===========================================
// Pending Dose Schemas
// ===========================================

export const doseStatusSchema = z.enum(['pending', 'taken', 'skipped', 'missed'])

export const updateDoseSchema = z.object({
  status: doseStatusSchema,
  notes: z.string().optional(),
}).refine(
  (data) => {
    // Notes are required for skipped and missed status (minimum 10 characters)
    if (data.status === 'skipped' || data.status === 'missed') {
      return data.notes && data.notes.trim().length >= 10
    }
    return true
  },
  {
    message: 'Se requieren instrucciones (mínimo 10 caracteres) para dosis omitidas',
    path: ['notes'],
  }
)

// Batch dose action schema
export const batchDoseActionSchema = z.enum(['taken', 'skipped'])

export const batchDosesSchema = z.object({
  doseIds: z.array(z.string()).min(1, 'Se requiere al menos una dosis').max(20, 'Máximo 20 dosis por solicitud'),
  action: batchDoseActionSchema,
  notes: z.string().optional(),
}).refine(
  (data) => {
    // Notes required for skipped action
    if (data.action === 'skipped') {
      return data.notes && data.notes.trim().length >= 10
    }
    return true
  },
  {
    message: 'Se requieren instrucciones (mínimo 10 caracteres) para omitir dosis',
    path: ['notes'],
  }
)

// ===========================================
// Checkup Schemas
// ===========================================

export const checkupTypeSchema = z.enum(['rutina', 'emergencia', 'seguimiento', 'especialista'])

export const createCheckupSchema = z.object({
  tipo: checkupTypeSchema,
  fecha: z.string({ message: 'Fecha es requerida' }),
  hora: optionalTimeField,
  veterinario: z.string().optional(),
  clinica: z.string().optional(),
  motivo: nonEmptyString('Motivo'),
  diagnostico: z.string().optional(),
  tratamiento: z.string().optional(),
  notas: z.string().optional(),
  proximaCita: z.string().optional(),
})

export const updateCheckupSchema = z.object({
  tipo: checkupTypeSchema.optional(),
  fecha: z.string().optional(),
  hora: nullableTimeField,
  veterinario: z.string().nullable().optional(),
  clinica: z.string().nullable().optional(),
  motivo: nonEmptyString('Motivo').optional(),
  diagnostico: z.string().nullable().optional(),
  tratamiento: z.string().nullable().optional(),
  notas: z.string().nullable().optional(),
  proximaCita: z.string().nullable().optional(),
})

// ===========================================
// Vaccine Schemas
// ===========================================

export const createVaccineSchema = z.object({
  nombre: nonEmptyString('Nombre de la vacuna'),
  fechaAplicacion: z.string({ message: 'Fecha de aplicación es requerida' }),
  hora: optionalTimeField,
  fechaProxima: z.string().optional(),
  lote: z.string().optional(),
  veterinario: z.string().optional(),
  notas: z.string().optional(),
})

export const updateVaccineSchema = z.object({
  nombre: nonEmptyString('Nombre de la vacuna').optional(),
  fechaAplicacion: z.string().optional(),
  hora: nullableTimeField,
  fechaProxima: z.string().nullable().optional(),
  lote: z.string().nullable().optional(),
  veterinario: z.string().nullable().optional(),
  notas: z.string().nullable().optional(),
})

// ===========================================
// Weight Log Schemas
// ===========================================

export const createWeightLogSchema = z.object({
  peso: z.number().positive({ message: 'El peso debe ser mayor a 0' }),
  fecha: z.string().optional(),
  notas: z.string().optional(),
})

// ===========================================
// Symptom Schemas
// ===========================================

export const symptomSeveritySchema = z.enum(['leve', 'moderado', 'severo'])

export const createSymptomSchema = z.object({
  descripcion: nonEmptyString('Descripción'),
  severidad: symptomSeveritySchema,
  fechaInicio: z.string({ message: 'Fecha de inicio es requerida' }),
  notas: z.string().optional(),
})

export const updateSymptomSchema = z.object({
  descripcion: nonEmptyString('Descripción').optional(),
  severidad: symptomSeveritySchema.optional(),
  fechaFin: z.string().nullable().optional(),
  notas: z.string().nullable().optional(),
})

// ===========================================
// Note Schemas
// ===========================================

export const createNoteSchema = z.object({
  contenido: nonEmptyString('Contenido'),
})

export const updateNoteSchema = z.object({
  contenido: nonEmptyString('Contenido'),
})

// ===========================================
// Clinical Event Schemas
// ===========================================

export const clinicalEventTypeSchema = z.enum([
  'consulta',
  'emergencia',
  'cirugia',
  'hospitalizacion',
  'procedimiento',
  'diagnostico',
  'tratamiento',
  'otro',
])

export const createClinicalEventSchema = z.object({
  tipo: clinicalEventTypeSchema,
  fecha: z.string({ message: 'Fecha es requerida' }),
  titulo: nonEmptyString('Título'),
  descripcion: z.string().optional(),
  veterinario: z.string().optional(),
  clinica: z.string().optional(),
  diagnostico: z.string().optional(),
  tratamiento: z.string().optional(),
  peso: z.number().positive().optional(),
  notas: z.string().optional(),
})

export const updateClinicalEventSchema = z.object({
  tipo: clinicalEventTypeSchema.optional(),
  fecha: z.string().optional(),
  titulo: nonEmptyString('Título').optional(),
  descripcion: z.string().nullable().optional(),
  veterinario: z.string().nullable().optional(),
  clinica: z.string().nullable().optional(),
  diagnostico: z.string().nullable().optional(),
  tratamiento: z.string().nullable().optional(),
  peso: z.number().positive().nullable().optional(),
  notas: z.string().nullable().optional(),
})

// ===========================================
// Pagination Schema
// ===========================================

export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
})

// ===========================================
// Type exports from schemas
// ===========================================

export type RequestMagicLinkInput = z.infer<typeof requestMagicLinkSchema>
export type VerifyMagicLinkInput = z.infer<typeof verifyMagicLinkSchema>
export type CreateUserInput = z.infer<typeof createUserSchema>
export type UpdateUserInput = z.infer<typeof updateUserSchema>
export type CreateCareGroupInput = z.infer<typeof createCareGroupSchema>
export type UpdateCareGroupInput = z.infer<typeof updateCareGroupSchema>
export type CreateInvitationInput = z.infer<typeof createInvitationSchema>
export type CreatePetInput = z.infer<typeof createPetSchema>
export type UpdatePetInput = z.infer<typeof updatePetSchema>
export type CreateMedicationInput = z.infer<typeof createMedicationSchema>
export type UpdateMedicationInput = z.infer<typeof updateMedicationSchema>
export type LogMedicationInput = z.infer<typeof logMedicationSchema>
export type UpdateDoseInput = z.infer<typeof updateDoseSchema>
export type BatchDosesInput = z.infer<typeof batchDosesSchema>
export type CreateCheckupInput = z.infer<typeof createCheckupSchema>
export type UpdateCheckupInput = z.infer<typeof updateCheckupSchema>
export type CreateVaccineInput = z.infer<typeof createVaccineSchema>
export type UpdateVaccineInput = z.infer<typeof updateVaccineSchema>
export type CreateWeightLogInput = z.infer<typeof createWeightLogSchema>
export type CreateSymptomInput = z.infer<typeof createSymptomSchema>
export type UpdateSymptomInput = z.infer<typeof updateSymptomSchema>
export type CreateNoteInput = z.infer<typeof createNoteSchema>
export type UpdateNoteInput = z.infer<typeof updateNoteSchema>
export type CreateClinicalEventInput = z.infer<typeof createClinicalEventSchema>
export type UpdateClinicalEventInput = z.infer<typeof updateClinicalEventSchema>
export type PaginationInput = z.infer<typeof paginationSchema>
